import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { FormTheme } from '../../types';
import { backgroundSx } from '../../utils/themeUtils';

export type ThemePresetCategory = 'Photo Backgrounds' | 'SaaS' | 'Gradient' | 'Solid';

export interface ThemePreset {
  id: string;
  category: ThemePresetCategory;
  name: string;
  swatch: string;
  theme: Partial<FormTheme>;
}

export const PresetThumbnail: React.FC<{ preset: ThemePreset }> = ({ preset }) => {
  const t = preset.theme;
  const radius = Math.min(t.borderRadius ?? 6, 10);
  return (
    <Box
      sx={{
        height: 130,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1.5,
        ...backgroundSx(t),
      }}
    >
      <Box
        sx={{
          width: '100%',
          background: t.formInputBackground || '#ffffff',
          border: '1px solid',
          borderColor: t.formInputBorder || '#e5e7eb',
          borderRadius: `${radius}px`,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.6,
        }}
      >
        <Box
          sx={{
            fontSize: 7,
            fontWeight: 700,
            color: t.heading1 || '#111827',
            textAlign: 'center',
          }}
        >
          Registration Form
        </Box>
        <Box sx={{ fontSize: 5, color: t.paragraph || '#6b7280', textAlign: 'center', mb: 0.3 }}>
          Fill out the form below to get started
        </Box>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              height: 9,
              borderRadius: `${Math.min(radius, 4)}px`,
              background: t.formInputBackground || '#ffffff',
              border: '1px solid',
              borderColor: t.formInputBorder || '#e5e7eb',
            }}
          />
        ))}
        <Box
          sx={{
            alignSelf: 'center',
            mt: 0.4,
            width: 34,
            height: 10,
            borderRadius: `${Math.min(radius, 5)}px`,
            background: t.actionButtonBackground || '#2563eb',
          }}
        />
      </Box>
    </Box>
  );
};

/** Render order for categories — photo presets lead since they're the most
    visually distinct pick and the one most likely to sell the feature. */
export const THEME_PRESET_CATEGORY_ORDER: ThemePresetCategory[] = [
  'SaaS',
  'Gradient',
  'Solid',
  'Photo Backgrounds',
];

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'saas-blue',
    category: 'Solid',
    name: 'SaaS Blue',
    swatch: '#2563eb',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#2563eb',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#2563eb',
      actionButtonBorder: '#2563eb',
      basicButtonText: '#2563eb',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#cbd5e1',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'clean-minimal',
    category: 'Solid',
    name: 'Clean Minimal',
    swatch: '#111827',
    theme: {
      heading1: '#111827',
      paragraph: '#4b5563',
      link: '#111827',
      label: '#111827',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#111827',
      actionButtonBorder: '#111827',
      basicButtonText: '#111827',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#d1d5db',
      formInputText: '#111827',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 28,
      formPaddingX: 28,
      background: '#ffffff',
    },
  },
  {
    id: 'corporate-navy',
    category: 'Solid',
    name: 'Corporate Navy',
    swatch: '#1e3a8a',
    theme: {
      heading1: '#0f172a',
      paragraph: '#334155',
      link: '#1d4ed8',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#1e3a8a',
      actionButtonBorder: '#1e3a8a',
      basicButtonText: '#1e3a8a',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#93c5fd',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#cbd5e1',
      formInputHeight: 42,
      borderRadius: 4,
      buttonFontSize: 15,
      buttonPaddingY: 10,
      buttonPaddingX: 22,
      formPaddingY: 28,
      formPaddingX: 28,
      background: '#ffffff',
    },
  },
  {
    id: 'soft-neutral',
    category: 'Solid',
    name: 'Soft Neutral',
    swatch: '#78716c',
    theme: {
      heading1: '#292524',
      paragraph: '#57534e',
      link: '#44403c',
      label: '#292524',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#44403c',
      actionButtonBorder: '#44403c',
      basicButtonText: '#44403c',
      basicButtonBackground: '#fafaf9',
      basicButtonBorder: '#d6d3d1',
      formInputText: '#292524',
      formInputBackground: '#fafaf9',
      formInputBorder: '#e7e5e4',
      formInputHeight: 44,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 24,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'trust-green',
    category: 'Solid',
    name: 'Trust Green',
    swatch: '#059669',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#059669',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#059669',
      actionButtonBorder: '#059669',
      basicButtonText: '#059669',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#a7f3d0',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'light-sky',
    category: 'Solid',
    name: 'Light Sky',
    swatch: '#0284c7',
    theme: {
      heading1: '#0c4a6e',
      paragraph: '#475569',
      link: '#0284c7',
      label: '#0c4a6e',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#0284c7',
      actionButtonBorder: '#0284c7',
      basicButtonText: '#0284c7',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#bae6fd',
      formInputText: '#0c4a6e',
      formInputBackground: '#f8fafc',
      formInputBorder: '#e2e8f0',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: '#ffffff',
    },
  },
  {
    id: 'graphite-dark',
    category: 'Solid',
    name: 'Graphite Dark',
    swatch: '#1f2937',
    theme: {
      heading1: '#f9fafb',
      paragraph: '#d1d5db',
      link: '#60a5fa',
      label: '#e5e7eb',
      actionButtonText: '#111827',
      actionButtonBackground: '#f9fafb',
      actionButtonBorder: '#f9fafb',
      basicButtonText: '#e5e7eb',
      basicButtonBackground: '#374151',
      basicButtonBorder: '#4b5563',
      formInputText: '#f9fafb',
      formInputBackground: '#374151',
      formInputBorder: '#4b5563',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#1f2937',
    },
  },
  {
    id: 'warm-professional',
    category: 'Solid',
    name: 'Warm Professional',
    swatch: '#c2410c',
    theme: {
      heading1: '#1c1917',
      paragraph: '#57534e',
      link: '#c2410c',
      label: '#1c1917',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#c2410c',
      actionButtonBorder: '#c2410c',
      basicButtonText: '#c2410c',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#fed7aa',
      formInputText: '#1c1917',
      formInputBackground: '#ffffff',
      formInputBorder: '#e7e5e4',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: '#ffffff',
    },
  },
  {
    id: 'azure-gradient',
    category: 'Gradient',
    name: 'Azure Gradient',
    swatch: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#2563eb',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
      actionButtonBorder: '#1e40af',
      basicButtonText: '#1e40af',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#bfdbfe',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
    },
  },
  {
    id: 'emerald-gradient',
    category: 'Gradient',
    name: 'Emerald Gradient',
    swatch: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#059669',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
      actionButtonBorder: '#047857',
      basicButtonText: '#047857',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#a7f3d0',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)',
    },
  },
  {
    id: 'slate-gradient',
    category: 'Gradient',
    name: 'Slate Gradient',
    swatch: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#334155',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
      actionButtonBorder: '#1e293b',
      basicButtonText: '#334155',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#cbd5e1',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 28,
      formPaddingX: 28,
      background: '#ffffff',
    },
  },
  {
    id: 'sunrise-gradient',
    category: 'Gradient',
    name: 'Sunrise Gradient',
    swatch: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
    theme: {
      heading1: '#1c1917',
      paragraph: '#57534e',
      link: '#ea580c',
      label: '#1c1917',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
      actionButtonBorder: '#ea580c',
      basicButtonText: '#ea580c',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#fed7aa',
      formInputText: '#1c1917',
      formInputBackground: '#ffffff',
      formInputBorder: '#e7e5e4',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: 'linear-gradient(180deg, #fffbeb 0%, #ffffff 100%)',
    },
  },
  {
    id: 'midnight-gradient',
    category: 'Gradient',
    name: 'Midnight Gradient',
    swatch: 'linear-gradient(135deg, #1e293b 0%, #020617 100%)',
    theme: {
      heading1: '#f8fafc',
      paragraph: '#cbd5e1',
      link: '#60a5fa',
      label: '#e2e8f0',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
      actionButtonBorder: '#1e40af',
      basicButtonText: '#e2e8f0',
      basicButtonBackground: '#1e293b',
      basicButtonBorder: '#334155',
      formInputText: '#f8fafc',
      formInputBackground: '#0f172a',
      formInputBorder: '#334155',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
    },
  },
  {
    id: 'indigo-saas',
    category: 'SaaS',
    name: 'Indigo SaaS',
    swatch: '#4f46e5',
    theme: {
      heading1: '#111827',
      paragraph: '#6b7280',
      link: '#4f46e5',
      label: '#111827',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#4f46e5',
      actionButtonBorder: '#4f46e5',
      basicButtonText: '#4f46e5',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#e0e7ff',
      formInputText: '#111827',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'violet-product',
    category: 'SaaS',
    name: 'Violet Product',
    swatch: '#7c3aed',
    theme: {
      heading1: '#1e1b4b',
      paragraph: '#5b5b7a',
      link: '#7c3aed',
      label: '#1e1b4b',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#7c3aed',
      actionButtonBorder: '#7c3aed',
      basicButtonText: '#7c3aed',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#ddd6fe',
      formInputText: '#1e1b4b',
      formInputBackground: '#ffffff',
      formInputBorder: '#e5e7eb',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'neutral-app',
    category: 'SaaS',
    name: 'Neutral App',
    swatch: '#4b5563',
    theme: {
      heading1: '#1f2937',
      paragraph: '#6b7280',
      link: '#374151',
      label: '#1f2937',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#1f2937',
      actionButtonBorder: '#1f2937',
      basicButtonText: '#374151',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#e5e7eb',
      formInputText: '#1f2937',
      formInputBackground: '#f9fafb',
      formInputBorder: '#e5e7eb',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: '#ffffff',
    },
  },
  {
    id: 'onyx-saas-dark',
    category: 'SaaS',
    name: 'Onyx SaaS Dark',
    swatch: '#0b0f19',
    theme: {
      heading1: '#f9fafb',
      paragraph: '#9ca3af',
      link: '#818cf8',
      label: '#e5e7eb',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#4f46e5',
      actionButtonBorder: '#4f46e5',
      basicButtonText: '#e5e7eb',
      basicButtonBackground: '#1a1f2e',
      basicButtonBorder: '#2b3242',
      formInputText: '#f9fafb',
      formInputBackground: '#1a1f2e',
      formInputBorder: '#2b3242',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#0b0f19',
    },
  },
  {
    id: 'fresh-mint-saas',
    category: 'SaaS',
    name: 'Fresh Mint',
    swatch: '#10b981',
    theme: {
      heading1: '#0f172a',
      paragraph: '#5b6472',
      link: '#0d9488',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#10b981',
      actionButtonBorder: '#10b981',
      basicButtonText: '#0d9488',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#a7f3d0',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#e2e8f0',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: '#ffffff',
    },
  },
  {
    id: 'signal-blue-saas',
    category: 'SaaS',
    name: 'Signal Blue',
    swatch: '#2563eb',
    theme: {
      heading1: '#0b1220',
      paragraph: '#54627a',
      link: '#2563eb',
      label: '#0b1220',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#2563eb',
      actionButtonBorder: '#2563eb',
      basicButtonText: '#2563eb',
      basicButtonBackground: '#f8fafc',
      basicButtonBorder: '#dbeafe',
      formInputText: '#0b1220',
      formInputBackground: '#ffffff',
      formInputBorder: '#dbe1ea',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 28,
      formPaddingX: 28,
      background: '#ffffff',
    },
  },
  {
    id: 'soft-card-saas',
    category: 'SaaS',
    name: 'Soft Card',
    swatch: '#f3f4f6',
    theme: {
      heading1: '#111827',
      paragraph: '#6b7280',
      link: '#4f46e5',
      label: '#111827',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#111827',
      actionButtonBorder: '#111827',
      basicButtonText: '#374151',
      basicButtonBackground: '#f3f4f6',
      basicButtonBorder: '#e5e7eb',
      formInputText: '#111827',
      formInputBackground: '#f9fafb',
      formInputBorder: '#e5e7eb',
      formInputHeight: 44,
      borderRadius: 12,
      buttonFontSize: 15,
      buttonPaddingY: 12,
      buttonPaddingX: 24,
      formPaddingY: 36,
      formPaddingX: 36,
      background: '#f9fafb',
    },
  },
  {
    id: 'insurance-teal',
    category: 'Solid',
    name: 'Insurance Teal',
    swatch: '#0f766e',
    theme: {
      heading1: '#134e4a',
      paragraph: '#475569',
      link: '#0f766e',
      label: '#134e4a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#0f766e',
      actionButtonBorder: '#0f766e',
      basicButtonText: '#0f766e',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#99f6e4',
      formInputText: '#134e4a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'legal-charcoal',
    category: 'Solid',
    name: 'Legal Charcoal',
    swatch: '#27272a',
    theme: {
      heading1: '#18181b',
      paragraph: '#52525b',
      link: '#27272a',
      label: '#18181b',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#27272a',
      actionButtonBorder: '#27272a',
      basicButtonText: '#27272a',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#d4d4d8',
      formInputText: '#18181b',
      formInputBackground: '#ffffff',
      formInputBorder: '#d4d4d8',
      formInputHeight: 42,
      borderRadius: 4,
      buttonFontSize: 15,
      buttonPaddingY: 10,
      buttonPaddingX: 22,
      formPaddingY: 28,
      formPaddingX: 28,
      background: '#ffffff',
    },
  },
  {
    id: 'real-estate-gold',
    category: 'Solid',
    name: 'Real Estate Gold',
    swatch: '#a16207',
    theme: {
      heading1: '#1c1917',
      paragraph: '#57534e',
      link: '#a16207',
      label: '#1c1917',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#a16207',
      actionButtonBorder: '#a16207',
      basicButtonText: '#a16207',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#fde68a',
      formInputText: '#1c1917',
      formInputBackground: '#ffffff',
      formInputBorder: '#e7e5e4',
      formInputHeight: 44,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 24,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'healthcare-clean',
    category: 'Solid',
    name: 'Healthcare Clean',
    swatch: '#0891b2',
    theme: {
      heading1: '#164e63',
      paragraph: '#475569',
      link: '#0891b2',
      label: '#164e63',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#0891b2',
      actionButtonBorder: '#0891b2',
      basicButtonText: '#0891b2',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#a5f3fc',
      formInputText: '#164e63',
      formInputBackground: '#f0fdff',
      formInputBorder: '#cffafe',
      formInputHeight: 44,
      borderRadius: 12,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'fintech-indigo-saas',
    category: 'SaaS',
    name: 'Fintech Indigo',
    swatch: '#3730a3',
    theme: {
      heading1: '#1e1b4b',
      paragraph: '#5b5b7a',
      link: '#3730a3',
      label: '#1e1b4b',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#3730a3',
      actionButtonBorder: '#3730a3',
      basicButtonText: '#3730a3',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#c7d2fe',
      formInputText: '#1e1b4b',
      formInputBackground: '#f8fafc',
      formInputBorder: '#e0e7ff',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'enterprise-slate-saas',
    category: 'SaaS',
    name: 'Enterprise Slate',
    swatch: '#334155',
    theme: {
      heading1: '#0f172a',
      paragraph: '#64748b',
      link: '#334155',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#0f172a',
      actionButtonBorder: '#0f172a',
      basicButtonText: '#334155',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#cbd5e1',
      formInputText: '#0f172a',
      formInputBackground: '#f8fafc',
      formInputBorder: '#e2e8f0',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: '#ffffff',
    },
  },
  {
    id: 'crimson-conversion-gradient',
    category: 'Gradient',
    name: 'Crimson Conversion',
    swatch: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#dc2626',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)',
      actionButtonBorder: '#7f1d1d',
      basicButtonText: '#7f1d1d',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#fecaca',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #fef2f2 0%, #ffffff 100%)',
    },
  },
  {
    id: 'teal-corporate-gradient',
    category: 'Gradient',
    name: 'Teal Corporate',
    swatch: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#0d9488',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)',
      actionButtonBorder: '#134e4a',
      basicButtonText: '#134e4a',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#99f6e4',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #f0fdfa 0%, #ffffff 100%)',
    },
  },
  {
    id: 'banking-navy',
    category: 'Solid',
    name: 'Banking Navy',
    swatch: '#172554',
    theme: {
      heading1: '#0c1a3a',
      paragraph: '#3f4d6b',
      link: '#1d4ed8',
      label: '#0c1a3a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#172554',
      actionButtonBorder: '#172554',
      basicButtonText: '#172554',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#c7d2fe',
      formInputText: '#0c1a3a',
      formInputBackground: '#ffffff',
      formInputBorder: '#cbd5e1',
      formInputHeight: 42,
      borderRadius: 4,
      buttonFontSize: 15,
      buttonPaddingY: 10,
      buttonPaddingX: 22,
      formPaddingY: 28,
      formPaddingX: 28,
      background: '#ffffff',
    },
  },
  {
    id: 'hr-recruiting-plum',
    category: 'Solid',
    name: 'HR Recruiting',
    swatch: '#86198f',
    theme: {
      heading1: '#3b0764',
      paragraph: '#581c87',
      link: '#86198f',
      label: '#3b0764',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#86198f',
      actionButtonBorder: '#86198f',
      basicButtonText: '#86198f',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#f5d0fe',
      formInputText: '#3b0764',
      formInputBackground: '#ffffff',
      formInputBorder: '#e9d5ff',
      formInputHeight: 44,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 24,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'analytics-cobalt-saas',
    category: 'SaaS',
    name: 'Analytics Cobalt',
    swatch: '#1d4ed8',
    theme: {
      heading1: '#0f172a',
      paragraph: '#64748b',
      link: '#1d4ed8',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#1d4ed8',
      actionButtonBorder: '#1d4ed8',
      basicButtonText: '#1d4ed8',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#bfdbfe',
      formInputText: '#0f172a',
      formInputBackground: '#f8fafc',
      formInputBorder: '#e2e8f0',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: '#ffffff',
    },
  },
  {
    id: 'onboarding-lime-saas',
    category: 'SaaS',
    name: 'Onboarding Lime',
    swatch: '#4d7c0f',
    theme: {
      heading1: '#1a2e05',
      paragraph: '#3f6212',
      link: '#4d7c0f',
      label: '#1a2e05',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#4d7c0f',
      actionButtonBorder: '#4d7c0f',
      basicButtonText: '#4d7c0f',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#d9f99d',
      formInputText: '#1a2e05',
      formInputBackground: '#f7fee7',
      formInputBorder: '#ecfccb',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 30,
      formPaddingX: 30,
      background: '#ffffff',
    },
  },
  {
    id: 'wealth-bronze-gradient',
    category: 'Gradient',
    name: 'Wealth Bronze',
    swatch: 'linear-gradient(135deg, #b45309 0%, #451a03 100%)',
    theme: {
      heading1: '#1c1917',
      paragraph: '#57534e',
      link: '#b45309',
      label: '#1c1917',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #b45309 0%, #451a03 100%)',
      actionButtonBorder: '#451a03',
      basicButtonText: '#451a03',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#fed7aa',
      formInputText: '#1c1917',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #fffbeb 0%, #ffffff 100%)',
    },
  },
  {
    id: 'tech-violet-gradient',
    category: 'Gradient',
    name: 'Tech Violet',
    swatch: 'linear-gradient(135deg, #7c3aed 0%, #2e1065 100%)',
    theme: {
      heading1: '#0f172a',
      paragraph: '#475569',
      link: '#7c3aed',
      label: '#0f172a',
      actionButtonText: '#ffffff',
      actionButtonBackground: 'linear-gradient(135deg, #7c3aed 0%, #2e1065 100%)',
      actionButtonBorder: '#2e1065',
      basicButtonText: '#2e1065',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#ddd6fe',
      formInputText: '#0f172a',
      formInputBackground: '#ffffff',
      formInputBorder: '#d1d5db',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 32,
      formPaddingX: 32,
      background: 'linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%)',
    },
  },
  {
    id: 'mountain-vista',
    category: 'Photo Backgrounds',
    name: 'Mountain Vista',
    swatch: 'linear-gradient(135deg, #64748b 0%, #1e293b 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e5e7eb',
      link: '#93c5fd',
      label: '#f3f4f6',
      actionButtonText: '#111827',
      actionButtonBackground: '#ffffff',
      actionButtonBorder: '#ffffff',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.12)',
      basicButtonBorder: 'rgba(255,255,255,0.4)',
      formInputText: '#111827',
      formInputBackground: 'rgba(255,255,255,0.92)',
      formInputBorder: 'rgba(255,255,255,0.6)',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 36,
      formPaddingX: 36,
      background: 'rgba(15,23,42,0.55)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'ocean-breeze',
    category: 'Photo Backgrounds',
    name: 'Ocean Breeze',
    swatch: 'linear-gradient(135deg, #38bdf8 0%, #0369a1 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e0f2fe',
      link: '#bae6fd',
      label: '#f0f9ff',
      actionButtonText: '#0369a1',
      actionButtonBackground: '#ffffff',
      actionButtonBorder: '#ffffff',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.14)',
      basicButtonBorder: 'rgba(255,255,255,0.45)',
      formInputText: '#0c4a6e',
      formInputBackground: 'rgba(255,255,255,0.94)',
      formInputBorder: 'rgba(255,255,255,0.6)',
      formInputHeight: 42,
      borderRadius: 12,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 36,
      formPaddingX: 36,
      background: 'rgba(3,105,161,0.45)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'soft-paper-texture',
    category: 'Photo Backgrounds',
    name: 'Soft Paper',
    swatch: '#f5f0e6',
    theme: {
      heading1: '#1f2937',
      paragraph: '#4b5563',
      link: '#92400e',
      label: '#1f2937',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#92400e',
      actionButtonBorder: '#92400e',
      basicButtonText: '#92400e',
      basicButtonBackground: '#ffffff',
      basicButtonBorder: '#e7d9c3',
      formInputText: '#1f2937',
      formInputBackground: 'rgba(255,255,255,0.9)',
      formInputBorder: '#e7d9c3',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 34,
      formPaddingX: 34,
      background: 'rgba(255,255,255,0.5)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1517697471339-4aa32003c11a?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'city-night',
    category: 'Photo Backgrounds',
    name: 'City Night',
    swatch: 'linear-gradient(135deg, #312e81 0%, #0f172a 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#cbd5e1',
      link: '#a5b4fc',
      label: '#e2e8f0',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#6366f1',
      actionButtonBorder: '#6366f1',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.1)',
      basicButtonBorder: 'rgba(255,255,255,0.35)',
      formInputText: '#111827',
      formInputBackground: 'rgba(255,255,255,0.94)',
      formInputBorder: 'rgba(255,255,255,0.5)',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 36,
      formPaddingX: 36,
      background: 'rgba(15,23,42,0.6)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'skyline-finance',
    category: 'Photo Backgrounds',
    name: 'Skyline Finance',
    swatch: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#dbeafe',
      link: '#93c5fd',
      label: '#eff6ff',
      actionButtonText: '#1e3a8a',
      actionButtonBackground: '#ffffff',
      actionButtonBorder: '#ffffff',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.12)',
      basicButtonBorder: 'rgba(255,255,255,0.4)',
      formInputText: '#0f172a',
      formInputBackground: 'rgba(255,255,255,0.94)',
      formInputBorder: 'rgba(255,255,255,0.6)',
      formInputHeight: 42,
      borderRadius: 8,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 36,
      formPaddingX: 36,
      background: 'rgba(15,23,42,0.65)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'executive-office',
    category: 'Photo Backgrounds',
    name: 'Executive Office',
    swatch: 'linear-gradient(135deg, #57534e 0%, #1c1917 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e7e5e4',
      link: '#fcd34d',
      label: '#f5f5f4',
      actionButtonText: '#1c1917',
      actionButtonBackground: '#fbbf24',
      actionButtonBorder: '#fbbf24',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.1)',
      basicButtonBorder: 'rgba(255,255,255,0.35)',
      formInputText: '#1c1917',
      formInputBackground: 'rgba(255,255,255,0.94)',
      formInputBorder: 'rgba(255,255,255,0.55)',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 36,
      formPaddingX: 36,
      background: 'rgba(28,25,23,0.6)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'real-estate-modern',
    category: 'Photo Backgrounds',
    name: 'Modern Property',
    swatch: 'linear-gradient(135deg, #78716c 0%, #292524 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e7e5e4',
      link: '#fdba74',
      label: '#f5f5f4',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#c2410c',
      actionButtonBorder: '#c2410c',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.12)',
      basicButtonBorder: 'rgba(255,255,255,0.4)',
      formInputText: '#1c1917',
      formInputBackground: 'rgba(255,255,255,0.93)',
      formInputBorder: 'rgba(255,255,255,0.6)',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 36,
      formPaddingX: 36,
      background: 'rgba(41,37,36,0.55)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'consulting-boardroom',
    category: 'Photo Backgrounds',
    name: 'Boardroom',
    swatch: 'linear-gradient(135deg, #334155 0%, #0f172a 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e2e8f0',
      link: '#7dd3fc',
      label: '#f1f5f9',
      actionButtonText: '#0f172a',
      actionButtonBackground: '#38bdf8',
      actionButtonBorder: '#38bdf8',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.1)',
      basicButtonBorder: 'rgba(255,255,255,0.35)',
      formInputText: '#0f172a',
      formInputBackground: 'rgba(255,255,255,0.94)',
      formInputBorder: 'rgba(255,255,255,0.55)',
      formInputHeight: 42,
      borderRadius: 6,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 34,
      formPaddingX: 34,
      background: 'rgba(15,23,42,0.6)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'healthcare-trust',
    category: 'Photo Backgrounds',
    name: 'Healthcare Trust',
    swatch: 'linear-gradient(135deg, #0e7490 0%, #164e63 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e0f2fe',
      link: '#a5f3fc',
      label: '#f0f9ff',
      actionButtonText: '#164e63',
      actionButtonBackground: '#ffffff',
      actionButtonBorder: '#ffffff',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.12)',
      basicButtonBorder: 'rgba(255,255,255,0.4)',
      formInputText: '#083344',
      formInputBackground: 'rgba(255,255,255,0.95)',
      formInputBorder: 'rgba(255,255,255,0.6)',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 34,
      formPaddingX: 34,
      background: 'rgba(22,78,99,0.55)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
  {
    id: 'startup-workspace',
    category: 'Photo Backgrounds',
    name: 'Startup Workspace',
    swatch: 'linear-gradient(135deg, #4338ca 0%, #1e1b4b 100%)',
    theme: {
      heading1: '#ffffff',
      paragraph: '#e0e7ff',
      link: '#c4b5fd',
      label: '#eef2ff',
      actionButtonText: '#ffffff',
      actionButtonBackground: '#4f46e5',
      actionButtonBorder: '#4f46e5',
      basicButtonText: '#ffffff',
      basicButtonBackground: 'rgba(255,255,255,0.12)',
      basicButtonBorder: 'rgba(255,255,255,0.4)',
      formInputText: '#1e1b4b',
      formInputBackground: 'rgba(255,255,255,0.94)',
      formInputBorder: 'rgba(255,255,255,0.6)',
      formInputHeight: 42,
      borderRadius: 10,
      buttonFontSize: 15,
      buttonPaddingY: 11,
      buttonPaddingX: 22,
      formPaddingY: 34,
      formPaddingX: 34,
      background: 'rgba(30,27,75,0.6)',
      backgroundImageUrl:
        'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
      backgroundImageSize: 'cover',
    },
  },
];

const ALL_FILTER = 'All' as const;
type CategoryFilter = ThemePresetCategory | typeof ALL_FILTER;

export const CategoryFilterChips: React.FC<{
  value: CategoryFilter;
  onChange: (value: CategoryFilter) => void;
}> = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
    {[ALL_FILTER, ...THEME_PRESET_CATEGORY_ORDER].map((cat) => {
      const selected = value === cat;
      return (
        <Box
          key={cat}
          component="button"
          type="button"
          onClick={() => onChange(cat)}
          sx={{
            cursor: 'pointer',
            border: '1px solid',
            borderColor: selected ? 'primary.main' : 'divider',
            borderRadius: 999,
            px: 1.5,
            py: 0.5,
            fontSize: 12,
            fontWeight: 600,
            color: selected ? 'primary.main' : 'text.secondary',
            bgcolor: selected ? (t) => `${t.palette.primary.main}14` : 'background.paper',
            transition: 'border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
            },
          }}
        >
          {cat}
        </Box>
      );
    })}
  </Box>
);

export const ThemePresetGrid: React.FC<{
  onSelect: (preset: ThemePreset) => void;
  selectedPresetId?: string | null;
}> = ({ onSelect, selectedPresetId }) => {
  const [filter, setFilter] = React.useState<CategoryFilter>(ALL_FILTER);

  const grouped = THEME_PRESET_CATEGORY_ORDER
    .filter((category) => filter === ALL_FILTER || filter === category)
    .map((category) => ({
      category,
      presets: THEME_PRESETS.filter((p) => p.category === category),
    })).filter((g) => g.presets.length > 0);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          Pick a starting look — every color, radius, and spacing below updates
          so you can fine-tune from there.
        </Typography>
      </Box>
      <Box sx={{ mb: 2.5 }}>
        <CategoryFilterChips value={filter} onChange={setFilter} />
      </Box>
      {grouped.map(({ category, presets }, i) => (
        <Box key={category} sx={{ mb: i === grouped.length - 1 ? 0 : 3 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              mb: 1.25,
            }}
          >
            {category}
          </Typography>
          <Grid container spacing={2}>
            {presets.map((preset) => {
              const selected = preset.id === selectedPresetId;
              return (
                <Grid key={preset.id} size={{ xs: 6, sm: 4 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => onSelect(preset)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2.5,
                      p: 0,
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      boxShadow: selected
                        ? (t) => `0 6px 18px ${t.palette.primary.main}26`
                        : '0 1px 2px rgba(15,23,42,0.04)',
                      transition:
                        'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: (t) => `0 6px 18px ${t.palette.primary.main}26`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <PresetThumbnail preset={preset} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        px: 1.5,
                        pt: 1.25,
                        pb: 1.5,
                        color: selected ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {preset.name}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
